import { DIFFERENT_COLOR, TYPE_ANALYTICS_MAP_VIEW } from 'common/defines/constants';
import { QUERY_KEY, STATUS_API } from 'constants/constants';
import { get, isEmpty, isEqual } from 'lodash';
import randomColor from 'randomcolor';
import { useCallback, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import {
  getDataLandUseAnalytic,
  getEnableSlopeArea,
  getPointDataLandUseAnalytic,
} from 'services/analytics/apiAnalyticsConfig.services';
import { getClientSettingsById } from 'services/clients/apiClient.services';
import { getColorsAnalyticLayer, saveColorsAnalyticLayer } from 'services/MapView/apiMapViewConfig.services';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { changePointDataLandUse, changePolygonAnalytics, mapViewSelector } from 'store/slices/mapViewSlice';
import SkeletonCharts from '../../../../../Common/SkeletonCharts';
import { LandUseCardLayer } from '../../Layer/LandUse';
import { SwipeableViewsCustom } from '../../SwipeableViewsCustom';
import { LandUsageChart } from './ChartLandUse/LandUsageChart';

export const LandUseAnalytics = () => {
  const dispatch = useAppDispatch();

  const { analyticId, polygonLandUseAnalytics, dateAnalyticSelected: uploadDate } = useAppSelector(mapViewSelector);
  const { clientId } = useParams();

  const { data: clientSetting } = useQuery(
    [QUERY_KEY.CLIENT_SETTINGS_BY_ID, clientId],
    () => getClientSettingsById(clientId || ''),
    {
      enabled: !!clientId,
    }
  );

  const { data: enableSlopeArea } = useQuery(
    [QUERY_KEY.ENABLE_SLOPE_AREA, analyticId],
    () => getEnableSlopeArea(analyticId || ''),
    {
      enabled: !!analyticId,
    }
  );

  const { data: mainData, status: statusMainData } = useQuery(
    [QUERY_KEY.LAND_USAGE_ANALYSIS, uploadDate, analyticId],
    () => getDataLandUseAnalytic(analyticId || '', uploadDate || ''),
    { enabled: !!uploadDate && !!analyticId && !!uploadDate }
  );

  const {
    data: colorsData,
    status,
    refetch,
  } = useQuery(
    [QUERY_KEY.COLOR_ANALYTIC_LAYER, clientSetting?._id, analyticId],
    () => getColorsAnalyticLayer(clientSetting?._id || '', analyticId || ''),
    { enabled: !!clientSetting?._id && !!analyticId }
  );

  const { mutate } = useMutation(
    (data: { analysisId: string; settingId: string; color: any }) => saveColorsAnalyticLayer(data),
    {
      onSuccess: () => {
        refetch().then();
      },
    }
  );

  useQuery([QUERY_KEY.LAND_USAGE_POINT_ANALYTIC, analyticId], () => getPointDataLandUseAnalytic(analyticId || ''), {
    enabled: !!analyticId,
    onSuccess: (response: any) => {
      const data = response.data;
      dispatch(changePointDataLandUse(data));
    },
  });

  const dataLandUsageAnalysis = useMemo(() => {
    return mainData?.data || [];
  }, [mainData]);

  const colorsDataAnalytics = useMemo(() => {
    if (status === STATUS_API.LOADING) {
      return null;
    }
    return get(colorsData, 'data.data[0].color');
  }, [status, colorsData]);

  const onSaveData = useCallback(
    (dataColor: any) => {
      if (analyticId && clientSetting) {
        mutate({ analysisId: analyticId, settingId: clientSetting._id, color: dataColor });
      }
    },
    [analyticId, clientSetting]
  );

  const mainDataColor = useMemo(() => {
    let colors: string[] = [];

    if (isEmpty(colorsDataAnalytics)) {
      colors = [...DIFFERENT_COLOR];
    } else {
      colors = colorsDataAnalytics;
    }

    if (dataLandUsageAnalysis.length > colors.length) {
      const extraColor = randomColor({
        count: dataLandUsageAnalysis.length - colors.length || 0,
        luminosity: 'bright',
      });
      colors = [...colors, ...extraColor];
    }
    if (status === STATUS_API.SUCCESS) {
      dispatch(
        changePolygonAnalytics(
          dataLandUsageAnalysis.map((_item: any, index: number) => ({
            ..._item,
            color: colors[index],
            visible: true,
          }))
        )
      );
    }

    return colors;
  }, [dataLandUsageAnalysis, dispatch, colorsDataAnalytics, status]);

  useEffect(() => {
    refetch().then();
  }, []);

  useEffect(() => {
    if (
      statusMainData !== STATUS_API.SUCCESS ||
      status !== STATUS_API.SUCCESS ||
      isEmpty(polygonLandUseAnalytics) ||
      !colorsDataAnalytics
    ) {
      return;
    }
    const colorNews = polygonLandUseAnalytics.map((_item: any) => _item.color);
    if (!isEqual(colorsDataAnalytics, colorNews)) {
      onSaveData(colorNews);
    }
  }, [colorsDataAnalytics, statusMainData, status, polygonLandUseAnalytics]);

  // save list initial color from json file
  useEffect(() => {
    const listColorFromJson = dataLandUsageAnalysis.map((item: { color: string }) => item.color).filter(Boolean);
    if (listColorFromJson?.length) {
      onSaveData(listColorFromJson);
    }
  }, [dataLandUsageAnalysis, onSaveData]);

  return (
    <SwipeableViewsCustom
      analyticName={TYPE_ANALYTICS_MAP_VIEW.LAND_USE_ANALYSIS}
      analyticTab={
        <>
          {isEmpty(dataLandUsageAnalysis) ? (
            <SkeletonCharts count={1} />
          ) : (
            <LandUsageChart
              uploadDate={uploadDate}
              analysisId={analyticId || ''}
              mainData={dataLandUsageAnalysis}
              mainColor={mainDataColor}
              clientSetting={clientSetting}
              enableSlopeArea={enableSlopeArea?.data}
            />
          )}
        </>
      }
      layerTab={<LandUseCardLayer labelCard={TYPE_ANALYTICS_MAP_VIEW.LAND_USE_ANALYSIS} />}
      isShowDownloadButton={!isEmpty(dataLandUsageAnalysis)}
    />
  );
};
